/**
 * Whether this page is currently showing originals instead of translations.
 *
 * One ticket carries a dozen translatable fields — subject, description,
 * resolution, every journal entry. Threading a boolean through all of them
 * would be noise, and letting each one toggle independently would leave the
 * page half translated, so the preference is per page and lives in context.
 */

import { createContext, type ReactNode, useContext, useMemo, useState } from "react";

interface TranslationPreference {
  /** True ⇒ render `original_text` wherever the server translated a field. */
  showOriginal: boolean;
  setShowOriginal: (next: boolean) => void;
}

const TranslationPreferenceContext = createContext<TranslationPreference>({
  showOriginal: false,
  setShowOriginal: () => undefined,
});

export interface TranslationPreferenceProviderProps {
  children: ReactNode;
}

export function TranslationPreferenceProvider({ children }: TranslationPreferenceProviderProps) {
  const [showOriginal, setShowOriginal] = useState(false);
  const value = useMemo(() => ({ showOriginal, setShowOriginal }), [showOriginal]);

  return (
    <TranslationPreferenceContext.Provider value={value}>
      {children}
    </TranslationPreferenceContext.Provider>
  );
}

export function useTranslationPreference(): TranslationPreference {
  return useContext(TranslationPreferenceContext);
}
